/**
 * `useDashboardRoute()` — parses the dashboard URL into a typed
 * `DashboardRouteState`. Single source of truth for "where am I in
 * the dashboard."
 *
 * The grammar:
 *   /dashboard/:folderId/:tab/:entityKind/:entityId/:detailTab?
 *
 * Path segments are positional; query params are transient overlays
 * (peek, scope, q, view). See `docs/architecture/dashboard-routing.md`.
 *
 * Components NEVER hand-roll the URL — they read state here, write
 * via `useDashboardNavigate()`.
 */

import { useMemo } from 'react'
import { useRouterState } from '@tanstack/react-router'

/**
 * Closed-but-extensible list of tab tokens. We don't validate at this
 * layer because tab sets are per folder-kind — the consumer (e.g.
 * ProcessFolderPanel) maps the raw string to its own typed tab union.
 * Unknown tabs are surfaced as-is; consumers fall back to their default.
 */
export type TabToken = string

/** Plural noun, lowercase, kebab-case. See doc for the canonical list. */
export type EntityKindToken = string

/** Sub-tab within an entity detail view. Per-kind enumeration. */
export type DetailTabToken = string

export type DashboardSection =
  | 'root'
  | 'recent'
  | 'personal'
  | 'myorg'
  | 'sharedwithme'
  | 'firm'
  | 'folder'

export interface DashboardRouteQuery {
  /** Peek panel state — when set, the side panel is open for this entity. */
  peek?: string
  /** Inventory / gallery scope filter id. */
  scope?: string
  /** Search query inside the current tab. */
  q?: string
  /** View mode (table vs cards, lens choice). */
  view?: string
}

export interface DashboardRouteState {
  /** Which top-level dashboard section we're in. */
  section: DashboardSection
  /** When section === 'folder' (or null in root sections). */
  folderId: string | null
  /** Workspace tab — null when on the bare folder URL. */
  tab: TabToken | null
  /** Drilled-into entity kind (`processes`, `controls`, ...). */
  entityKind: EntityKindToken | null
  /** Drilled-into entity id. */
  entityId: string | null
  /** Sub-tab within the entity detail. */
  detailTab: DetailTabToken | null
  /** Transient overlay state from search params. */
  query: DashboardRouteQuery
  /** The raw decoded path segments (useful for debugging). */
  rawSegments: readonly string[]
}

const ROOT_SECTIONS: Record<string, DashboardSection> = {
  '': 'root',
  recent: 'recent',
  personal: 'personal',
  myorg: 'myorg',
  sharedwithme: 'sharedwithme',
  firm: 'firm',
}

/**
 * Parse the location into structured route state. Path-only —
 * search-param decoding is layered on top.
 */
export function parseDashboardPath(pathname: string): Omit<DashboardRouteState, 'query'> {
  let p = pathname || '/'
  // Dashboard router uses basepath '/dashboard'; the location.pathname
  // we read in tests may or may not include it. Strip if present.
  if (p.startsWith('/dashboard')) p = p.slice('/dashboard'.length) || '/'
  const segments = p.split('/').filter(Boolean)
  const first = segments[0] ?? ''

  // Root sections — no folder context.
  if (first in ROOT_SECTIONS) {
    return {
      section: ROOT_SECTIONS[first]!,
      folderId: null,
      tab: null,
      entityKind: null,
      entityId: null,
      detailTab: null,
      rawSegments: segments,
    }
  }

  // Folder view: /:folderId[/:tab[/:entityKind/:entityId[/:detailTab]]]
  const folderId = first
  const tab = (segments[1] ?? null) as TabToken | null
  const entityKind = (segments[2] ?? null) as EntityKindToken | null
  const entityId = segments[3] ? decodeURIComponent(segments[3]) : null
  const detailTab = (segments[4] ?? null) as DetailTabToken | null

  return {
    section: 'folder',
    folderId,
    tab,
    entityKind,
    entityId,
    detailTab,
    rawSegments: segments,
  }
}

function parseQuery(search: unknown): DashboardRouteQuery {
  if (!search || typeof search !== 'object') return {}
  const s = search as Record<string, unknown>
  const out: DashboardRouteQuery = {}
  if (typeof s.peek === 'string' && s.peek) out.peek = s.peek
  if (typeof s.scope === 'string' && s.scope) out.scope = s.scope
  if (typeof s.q === 'string' && s.q) out.q = s.q
  if (typeof s.view === 'string' && s.view) out.view = s.view
  return out
}

export function useDashboardRoute(): DashboardRouteState {
  const state = useRouterState()
  const pathname = state.location.pathname
  const search = state.location.search

  return useMemo<DashboardRouteState>(() => {
    const parsedPath = parseDashboardPath(pathname)
    return {
      ...parsedPath,
      query: parseQuery(search),
    }
  }, [pathname, search])
}
